import React, { startTransition, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Popover } from '@mui/material';
import { Close as CloseIcon, ContactSupportOutlined } from '@mui/icons-material';

import { SIDE_NAVIGATION_TABS, ReloadIcon, DownloadIcon } from './constants';
import { AppDownloadSwitcher } from '../AppDownloadSwitcher';
import { CustomTooltip } from '../common';
import { TeamBetaIntroModal } from '../Team';
import { useRefreshData } from '../../hooks';
import { Path } from '../../routing';
import { useFeatureFlag } from '../../utils';
import './SideNavigation.scss';

const SideNavigation = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { refreshData, isReSyncing } = useRefreshData();
  const { isTeamPlanUser } = useFeatureFlag();
  const [downloadPopoverAnchorEl, setDownloadPopoverAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  const contactMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const contactMenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isTeamBetaIntroOpen, setIsTeamBetaIntroOpen] = useState(false);
  const tabs = SIDE_NAVIGATION_TABS;

  const isDownloadPopoverOpen = Boolean(downloadPopoverAnchorEl);
  const downloadPopoverId = isDownloadPopoverOpen ? 'side-navigation-download-popover' : undefined;
  const contactMenuId = isContactMenuOpen ? 'side-navigation-contact-menu' : undefined;

  const isTabActive = (tabPath: string) => {
    if (tabPath === Path.DASHBOARD && pathname === Path.HOME) {
      return true;
    }
    return pathname === tabPath || pathname.startsWith(`${tabPath}/`);
  };

  const handleRefreshData = () => {
    void refreshData();
  };

  const handleOpenDownloadPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDownloadPopoverAnchorEl(event.currentTarget);
  };

  const handleCloseDownloadPopover = () => {
    setDownloadPopoverAnchorEl(null);
  };

  const clearContactMenuCloseTimer = () => {
    if (contactMenuCloseTimerRef.current) {
      clearTimeout(contactMenuCloseTimerRef.current);
      contactMenuCloseTimerRef.current = null;
    }
  };

  const openContactMenu = () => {
    clearContactMenuCloseTimer();
    setIsContactMenuOpen(true);
  };

  const scheduleCloseContactMenu = () => {
    contactMenuCloseTimerRef.current = setTimeout(() => {
      setIsContactMenuOpen(false);
    }, 200);
  };

  const handleCloseContactMenu = () => {
    clearContactMenuCloseTimer();
    setIsContactMenuOpen(false);
  };

  const handleHelpAndFaqs = () => {
    window.open(
      'https://app.notion.com/p/demind-inc/Customer-Support-26f7c97113e68070a6e0e6a838da5f4a?source=copy_link',
      '_blank'
    );
    handleCloseContactMenu();
  };

  const handleShareFeedback = () => {
    window.open('https://feedback.lifestack.ai', '_blank');
    handleCloseContactMenu();
  };

  const handleContactSupport = () => {
    window.open('mailto:contact@demind-inc.com', '_blank');
    handleCloseContactMenu();
  };

  return (
    <div className="side-navigation-container">
      <aside className="side-navigation" aria-label="Side navigation">
        {tabs.map((tab) => {
          const isSelected = isTabActive(tab.path);
          const Icon = isSelected ? tab.iconFilled : tab.iconOutlined;
          return (
            <button
              key={tab.title}
              type="button"
              className={`side-navigation__icon-button ${isSelected ? 'side-navigation__icon-button--selected' : ''}`}
              aria-label={tab.title}
              onClick={() => {
                if (tab.path === Path.TEAM && !isTeamPlanUser) {
                  setIsTeamBetaIntroOpen(true);
                  return;
                }
                startTransition(() => {
                  navigate(tab.path);
                });
              }}
            >
              <Icon className="side-navigation__svg-icon" />
            </button>
          );
        })}

        <div className="side-navigation__bottom">
          <CustomTooltip
            title={isReSyncing ? 'Refreshing' : 'Click to refresh data'}
            placement="right"
            className="side-navigation__bottom-tooltip"
          >
            <span className="side-navigation__tooltip-trigger">
              <button
                type="button"
                className="side-navigation__icon-button"
                aria-label="Reload"
                onClick={handleRefreshData}
                disabled={isReSyncing}
              >
                <ReloadIcon
                  className={`side-navigation__svg-icon ${isReSyncing ? 'side-navigation__svg-icon--rotating' : ''}`}
                />
              </button>
            </span>
          </CustomTooltip>
          <button
            ref={contactMenuAnchorRef}
            type="button"
            className="side-navigation__icon-button"
            aria-label="Help and support"
            aria-controls={contactMenuId}
            aria-haspopup="menu"
            aria-expanded={isContactMenuOpen}
            onMouseEnter={openContactMenu}
            onMouseLeave={scheduleCloseContactMenu}
            onFocus={openContactMenu}
          >
            <ContactSupportOutlined className="side-navigation__mui-icon" />
          </button>
          <CustomTooltip
            title="Install Mobile/Mac App"
            placement="right"
            className="side-navigation__bottom-tooltip"
          >
            <span className="side-navigation__tooltip-trigger">
              <button
                type="button"
                className="side-navigation__icon-button"
                aria-label="Install Mobile/Mac App"
                onClick={handleOpenDownloadPopover}
                aria-describedby={downloadPopoverId}
              >
                <DownloadIcon className="side-navigation__svg-icon" />
              </button>
            </span>
          </CustomTooltip>
        </div>
        <Popover
          id={contactMenuId}
          anchorEl={contactMenuAnchorRef.current}
          open={isContactMenuOpen}
          onClose={(_event, reason) => {
            if (reason === 'escapeKeyDown') {
              handleCloseContactMenu();
            }
          }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          className="side-navigation__contact-menu"
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableScrollLock
          slotProps={{
            root: {
              sx: { pointerEvents: 'none' },
            },
            paper: {
              className: 'side-navigation__contact-menu-paper',
              onMouseEnter: clearContactMenuCloseTimer,
              onMouseLeave: scheduleCloseContactMenu,
              sx: { pointerEvents: 'auto' },
            },
          }}
        >
          <div className="side-navigation__contact-menu-list" role="menu">
            <button
              type="button"
              role="menuitem"
              className="side-navigation__contact-menu-item"
              onClick={handleHelpAndFaqs}
            >
              Help & FAQs
            </button>
            <button
              type="button"
              role="menuitem"
              className="side-navigation__contact-menu-item"
              onClick={handleShareFeedback}
            >
              Share feedback
            </button>
            <button
              type="button"
              role="menuitem"
              className="side-navigation__contact-menu-item"
              onClick={handleContactSupport}
            >
              Contact Support
            </button>
          </div>
        </Popover>
        <Popover
          id={downloadPopoverId}
          open={isDownloadPopoverOpen}
          anchorEl={downloadPopoverAnchorEl}
          onClose={handleCloseDownloadPopover}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          className="side-navigation__download-popover"
        >
          <div className="side-navigation__download-modal">
            <div className="side-navigation__download-modal__header">
              <p>Install Mobile/Mac App</p>
              <IconButton
                onClick={handleCloseDownloadPopover}
                className="side-navigation__download-modal__close-button"
              >
                <CloseIcon className="side-navigation__download-modal__close-button__icon" />
              </IconButton>
            </div>
            <div className="side-navigation__download-modal__content">
              <AppDownloadSwitcher />
            </div>
          </div>
        </Popover>
      </aside>
      <TeamBetaIntroModal
        open={isTeamBetaIntroOpen}
        onContinue={() => setIsTeamBetaIntroOpen(false)}
        showScheduleLink
      />
    </div>
  );
};

export default SideNavigation;
